"use client";

import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

const navItems = [
  { label: "Dashboard", active: true },
  { label: "Pessoas" },
  { label: "Ministérios" },
  { label: "Finanças" },
  { label: "Kids" },
  { label: "Atividades" },
  { label: "Cantina" },
  { label: "Relatórios" },
  { label: "Bio Links" },
  { label: "Configurações" },
];

const statCards = [
  {
    label: "Pessoas Ativas",
    value: "2168",
    sub: "2512 total de",
    subLink: "pessoas",
    iconClass: "bg-chart-1/15 text-chart-1",
  },
  {
    label: "Total de Visitantes",
    value: "573",
    sub: "Visitantes em",
    subLink: "março",
    iconClass: "bg-chart-3/15 text-chart-3",
  },
  {
    label: "Aniversariantes",
    value: "19",
    sub: "Aniversariantes",
    subLink: "hoje",
    iconClass: "bg-chart-5/15 text-chart-5",
  },
  {
    label: "Próximos Eventos",
    value: "5",
    sub: "Eventos",
    subLink: "programados",
    iconClass: "bg-chart-2/15 text-chart-2",
  },
];

/* ── Sub-components ─────────────────────────────────────────────────── */

function VisitorChart() {
  const bars = [43, 52, 37, 62, 47, 100];
  const labels = ["out", "nov", "dez", "jan", "fev", "mar"];
  return (
    <div className="bg-card rounded-2xl border border-border/40 p-4 flex flex-col">
      <div className="flex items-start justify-between mb-1">
        <div>
          <p className="text-xs font-semibold text-foreground">Visitantes</p>
          <p className="text-[10px] text-muted-foreground">
            outubro de 2025 - março de 2026
          </p>
        </div>
        <div className="w-6 h-6 rounded-md border border-border/40 grid place-items-center">
          <div className="w-2.5 h-2 bg-muted-foreground/20 rounded-[2px]" />
        </div>
      </div>

      <div className="flex items-end gap-1.5 mt-3" style={{ height: 80 }}>
        {bars.map((h, i) => (
          <div key={i} className="flex-1 flex flex-col justify-end h-full">
            <div
              className="bg-primary/75 rounded-t-sm w-full"
              style={{ height: `${h}%` }}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-2">
        {labels.map((m) => (
          <span
            key={m}
            className="flex-1 text-center text-[9px] text-muted-foreground"
          >
            {m}
          </span>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-border/30">
        <div className="flex items-center gap-1 mb-0.5">
          <TrendingUp className="w-3 h-3 text-status-good" />
          <span className="text-[10px] font-medium text-status-good">
            Subiu 100% este mês
          </span>
        </div>
        <p className="text-[9px] text-muted-foreground">
          Mostrando total de visitantes nos últimos 6 meses
        </p>
      </div>
    </div>
  );
}

function MembersChart() {
  // area chart: dramatic spike at the end (oct→mar)
  const pts = [
    [0, 95],
    [20, 93],
    [40, 90],
    [60, 87],
    [80, 70],
    [100, 8],
  ];
  const line = pts.map(([x, y]) => `${x},${y}`).join(" ");
  const area = `${line} 100,100 0,100`;

  return (
    <div className="bg-card rounded-2xl border border-border/40 p-4 flex flex-col">
      <div className="flex items-start justify-between mb-1">
        <div>
          <p className="text-xs font-semibold text-foreground">Membros</p>
          <p className="text-[10px] text-muted-foreground">
            outubro de 2025 - março de 2026
          </p>
        </div>
        <div className="w-6 h-6 rounded-md border border-border/40 grid place-items-center">
          <div className="w-2.5 h-2 bg-muted-foreground/20 rounded-[2px]" />
        </div>
      </div>

      <div className="mt-3 text-primary" style={{ height: 80 }}>
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="w-full h-full"
        >
          <defs>
            <linearGradient id="hpMG" x1="0" y1="0" x2="0" y2="1">
              {/* `currentColor`, não `hsl(var(--primary))`: no Tailwind 4 o
                  token já é uma cor completa (`--primary: #1a4270`), então
                  `hsl(...)` em volta gerava valor inválido e o gráfico saía
                  preto. A cor vem do `text-primary` no wrapper do svg. */}
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <polygon points={area} fill="url(#hpMG)" />
          <polyline
            points={line}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="flex justify-between mt-2">
        {["out", "nov", "dez", "jan", "fev", "mar"].map((m) => (
          <span
            key={m}
            className="flex-1 text-center text-[9px] text-muted-foreground"
          >
            {m}
          </span>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-border/30">
        <div className="flex items-center gap-1 mb-0.5">
          <TrendingUp className="w-3 h-3 text-status-good" />
          <span className="text-[10px] font-medium text-status-good">
            Subiu 871.4% este mês
          </span>
        </div>
        <p className="text-[9px] text-muted-foreground">
          Mostrando total de membros nos últimos 6 meses
        </p>
      </div>
    </div>
  );
}

function AcompanhamentosWidget() {
  return (
    <div className="bg-card rounded-2xl border border-border/40 p-4 flex flex-col gap-3">
      <div>
        <p className="text-xs font-semibold text-foreground">Acompanhamentos</p>
        <p className="text-[9px] text-muted-foreground leading-snug mt-0.5">
          Visitantes com contato agendado para hoje e atrasados
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-2xl bg-status-critical/8 border border-status-critical/20 flex flex-col items-center justify-center py-3">
          <span className="text-2xl font-bold text-status-critical leading-none">
            6
          </span>
          <span className="text-[9px] text-status-critical mt-0.5">Atrasados</span>
        </div>
        <div className="rounded-2xl bg-secondary/40 border border-border/30 flex flex-col items-center justify-center py-3">
          <span className="text-2xl font-bold text-foreground leading-none">
            9
          </span>
          <span className="text-[9px] text-muted-foreground mt-0.5">Hoje</span>
        </div>
      </div>

      <div>
        <p className="text-[9px] text-muted-foreground/60 uppercase tracking-widest font-medium mb-1.5">
          Na Agenda
        </p>
        <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-secondary/30">
          <div>
            <p className="text-[10px] font-medium text-foreground">Carlos</p>
            <p className="text-[9px] text-muted-foreground">30 de março</p>
          </div>
          <div className="w-4 h-4 rounded-full border border-border/50" />
        </div>
      </div>

      {/* `span`, não `button`: este preview é renderizado dentro do <button>
          de play do hero (hero-video.tsx) e botão dentro de botão é HTML
          inválido — o React reclamava de hydration e o toque na área podia
          não chegar ao play. Aqui é decoração, não controle. */}
      <span className="block w-full text-center text-[10px] border border-border/40 rounded-xl py-2 text-muted-foreground">
        Ver todos os visitantes
      </span>
    </div>
  );
}

function EventosWidget() {
  return (
    <div className="bg-card rounded-2xl border border-border/40 p-4">
      <p className="text-xs font-semibold text-foreground">Eventos</p>
      <p className="text-[9px] text-muted-foreground mb-3">
        Próximos eventos que irão acontecer
      </p>
      <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-secondary/30">
        <div>
          <p className="text-[10px] font-medium text-foreground">
            Conferência Avivamento 2026
          </p>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-[9px] text-muted-foreground">
              📅 3 de abr
            </span>
            <span className="text-[9px] text-muted-foreground">🕐 11:00</span>
          </div>
        </div>
        {/* idem: decoração dentro do <button> de play do hero */}
        <span className="text-[9px] border border-border/40 rounded-lg px-2 py-1 text-muted-foreground bg-background shrink-0 ml-2">
          Ver inscrições
        </span>
      </div>
    </div>
  );
}

function FinanceiroWidget() {
  return (
    <div className="bg-card rounded-2xl border border-border/40 p-4">
      <p className="text-xs font-semibold text-foreground">Resumo Financeiro</p>
      <p className="text-[9px] text-muted-foreground mb-3">
        Movimentação do mês atual
      </p>
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-status-good/5 border border-status-good/15">
        <div className="w-7 h-7 rounded-full bg-status-good/15 grid place-items-center shrink-0">
          <TrendingUp className="w-3.5 h-3.5 text-status-good" />
        </div>
        <div>
          <p className="text-[9px] text-muted-foreground">Entradas</p>
          <p className="text-sm font-bold text-status-good">
            R$ 1.914,04
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Main export ────────────────────────────────────────────────────── */

export function DashboardPreview() {
  return (
    <div className="flex w-full bg-background text-foreground overflow-hidden">
      {/* ── Sidebar ───────────────────────────────────────────── */}
      <div className="hidden sm:flex w-40 shrink-0 border-r border-border/30 flex-col py-3 gap-0.5 bg-background">
        {/* Logo */}
        <div className="px-3 pb-3 mb-1 flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-primary/20 grid place-items-center text-primary font-bold text-sm shrink-0">
            E
          </div>
          <div className="min-w-0">
            <p className="text-[9px] font-semibold text-foreground truncate leading-tight">
              Central da Esperança
            </p>
            <p className="text-[8px] text-muted-foreground">
              Gestão Eclesiástica
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mx-2 mb-2 px-2.5 py-1.5 rounded-lg border border-border/30 bg-secondary/30 flex items-center gap-1.5">
          <span className="text-muted-foreground/40 text-[9px]">Buscar...</span>
          <span className="ml-auto text-[8px] text-muted-foreground/30 border border-border/30 rounded px-1">
            ⌘K
          </span>
        </div>

        <p className="px-3 text-[8px] text-muted-foreground/50 uppercase tracking-widest font-medium mb-0.5">
          Navegação
        </p>

        {navItems.map((item) => (
          <div
            key={item.label}
            className={cn(
              "mx-2 px-2.5 py-1.5 rounded-lg text-[10px] font-medium cursor-pointer transition-colors",
              item.active
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground",
            )}
          >
            {item.label}
          </div>
        ))}

        {/* User */}
        <div className="mt-auto pt-2 border-t border-border/20 mx-2">
          <div className="flex items-center gap-1.5 px-1.5 py-1.5">
            <div className="w-6 h-6 rounded-full bg-primary/20 grid place-items-center text-primary text-[9px] font-bold shrink-0">
              V
            </div>
            <div className="min-w-0">
              <p className="text-[9px] font-medium text-foreground truncate">
                Vinicius
              </p>
              <p className="text-[7px] text-muted-foreground truncate">
                vinnicorrea7@gma…
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main ──────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col bg-background/95">
        {/* Topbar */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/20">
          <span className="text-[10px] text-muted-foreground/50">Início</span>
        </div>

        <div className="flex-1 p-4 md:p-5 flex flex-col gap-4">
          {/* Page header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-base font-semibold text-foreground leading-none">
                Central da Esperança
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Piracicaba, SP
              </p>
            </div>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-card rounded-2xl border border-border/40 p-3.5 flex flex-col gap-1"
              >
                <div className="flex items-start justify-between">
                  <p className="text-[10px] text-muted-foreground font-medium">
                    {card.label}
                  </p>
                  <div
                    className={cn(
                      "w-6 h-6 rounded-lg grid place-items-center shrink-0",
                      card.iconClass,
                    )}
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-current opacity-60" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-foreground leading-none">
                  {card.value}
                </p>
                <p className="text-[9px] text-muted-foreground">
                  {card.sub}{" "}
                  <span className="text-primary/70">{card.subLink}</span>
                </p>
              </div>
            ))}
          </div>

          {/* Charts row — no mobile entra só o primeiro gráfico. Os três lado a
              lado viram três blocos empilhados, e é isso que fazia o product
              shot do hero passar de três telas de altura. `md:contents` devolve
              os filhos ao grid de 3 colunas a partir do desktop. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <VisitorChart />
            <div className="hidden md:contents">
              <MembersChart />
              <AcompanhamentosWidget />
            </div>
          </div>

          {/* Bottom row — mesma razão: só a partir do desktop */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-3">
            <EventosWidget />
            <FinanceiroWidget />
          </div>
        </div>
      </div>
    </div>
  );
}
